import { DEFAULT_CURRENCY_CODE, ErrorHandler, NgModule, inject, provideAppInitializer, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CookieModule, CookieService } from 'ngx-cookie';
import { initializeApplication } from './app-initializer.factory';
import { LocalAuthService } from './auth/local-auth.service';
import { JwtInterceptor } from './auth/token.interceptor';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TrackJsErrorHandler } from './trackjs.handler';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DynamicModule } from 'ng-dynamic-component';
import { EmailTemplateModule } from './component-modules/email-template/email-template.module';
import { McCfLoginComponent } from './mc-cf-login/mc-cf-login.component';
import { MissionControlModule } from './routed-modules/mission-control/mission-control.module';
import { createApollo } from './graphql-provider';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { providePrimeNG } from 'primeng/config';
import { Theme } from 'src/theme';
import { ButtonModule } from 'primeng/button';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { NavigationService } from './utility/navigation.service';
import { GoogleLoginProvider, GoogleSigninButtonModule, SocialLoginModule } from '@abacritt/angularx-social-login'
import { environment } from '../environments/environment';
import { NotesModule } from './component-modules/notes/notes.module';
import { RootsNotesModule } from './routed-modules/roots/roots-notes.module';
import { provideRouter } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { DialogModule } from 'primeng/dialog';
import { ImageLightboxComponent } from './ui/image-lightbox/image-lightbox.component';
import { API_CONFIG, INCIDENT_FORM_FIELD_PROVIDER } from '@shared';
import { ConfigService } from './config.service';
import { FormFieldService } from './api/form-field.service';

@NgModule({
    declarations: [
        AppComponent,
        McCfLoginComponent
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        NoopAnimationsModule,
        AppRoutingModule,
        CookieModule.withOptions(),
        ReactiveFormsModule,
        FontAwesomeModule,
        FormsModule,
        ToastModule,
        DynamicModule,
        EmailTemplateModule,
        MissionControlModule,
        ButtonModule,
        SocialLoginModule,
        GoogleSigninButtonModule,
        MissionControlModule,
        NotesModule,
        RootsNotesModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
          enabled: !isDevMode(),
          // Register the ServiceWorker as soon as the application is stable
          // or after 30 seconds (whichever comes first).
          registrationStrategy: 'registerWhenStable:30000'
        }),
        DialogModule,
        ImageLightboxComponent
    ],
    providers: [
        {
            provide: API_CONFIG,
            useFactory: () => {
                const config = inject(ConfigService);
                return {
                    dataApiUrl: config.dataApiUrl,
                    mcapiKey: config.mcapiKey,
                    mcHost: config.mcHost,
                };
            },
        },
        { provide: INCIDENT_FORM_FIELD_PROVIDER, useExisting: FormFieldService },
        provideAppInitializer(() => {
            //injecting navigation service here to make sure it's instantiated on startup
            const initializerFn = (initializeApplication)(inject(CookieService), inject(LocalAuthService), inject(NavigationService));
            return initializerFn();
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JwtInterceptor,
            multi: true
        },
        {
            provide: ErrorHandler, useClass: TrackJsErrorHandler
        },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'USD' },
        MessageService,
        provideCharts(withDefaultRegisterables([])),
        provideHttpClient(withInterceptorsFromDi()),
        providePrimeNG({
            theme: {
                preset: Theme.McTheme,
                options: {
                    darkModeSelector: '.dark-mode',
                    cssLayer: {
                        name: 'primeng',
                        order: 'base, primeng, primeng-custom, utilities, components, theme'
                    }
                }
            }
        }),
        provideApollo(() => {
            const httpLink = inject(HttpLink)
            const localAuth = inject(LocalAuthService)

            return createApollo(httpLink, localAuth)
        }),
        {
            provide: 'SocialAuthServiceConfig',
            useValue: {
                autoLogin: false,
                providers: [
                    {
                        id: GoogleLoginProvider.PROVIDER_ID,
                        provider: new GoogleLoginProvider(
                            environment.config.GOOGLE_SIGN_IN_CLIENT_ID,
                            {
                                oneTapEnabled: false,
                                scopes: ['email', 'profile']
                            }
                        )
                    }
                ],
                onError: (err: any) => {
                    console.error(err);
                }
            }
        }
    ]
})
export class AppModule { }
